import React from "react";
import { Modal, Tag } from "antd";
import moment from "moment";

import EmployeeAvatar from "../EmployeeAvatar";
import {
  RULE_LABELS,
  WEEKDAY_LABELS,
  groupHistoryExceptions,
} from "../../helpers/payrollDisplay.helper";

import type { EmployeeLite } from "../../helpers/payrollDisplay.helper";
import type { PayrollDownloadHistoryRow } from "../../types";

interface DownloadDetailModalProps {
  open: boolean;
  onClose: () => void;
  record: PayrollDownloadHistoryRow | null;
  employeeMap: Map<number, EmployeeLite>;
  departmentMap: Map<number, string>;
}

const CARD_STYLE: React.CSSProperties = {
  background: "#ffffff",
  border: "1px solid #e2e8f0",
  borderRadius: 12,
  padding: 18,
};

const SectionTitle: React.FC<{ icon: string; children: React.ReactNode }> = ({
  icon,
  children,
}) => (
  <div
    className="d-flex align-items-center gap-2 mb-3"
    style={{ fontWeight: 600, color: "#0f172a", fontSize: 15 }}
  >
    <i className={icon} style={{ color: "#2563eb", fontSize: 18 }} />
    {children}
  </div>
);

const Label: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div
    className="text-uppercase mb-1"
    style={{ fontSize: 11, letterSpacing: 0.4, color: "#94a3b8", fontWeight: 600 }}
  >
    {children}
  </div>
);

const formatBytes = (bytes: number | null) => {
  if (!bytes) return "—";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
};

export const DownloadDetailModal: React.FC<DownloadDetailModalProps> = ({
  open,
  onClose,
  record,
  employeeMap,
  departmentMap,
}) => {
  if (!record) return null;

  const byEmp = employeeMap.get(Number(record.downloaded_by_user_id));
  const byName = record.downloaded_by_name || byEmp?.name || "Unknown";
  const cfg = record.filter_config || {};
  const groups = groupHistoryExceptions(record.exceptions);

  const deptChips = (ids: number[]) =>
    !ids || ids.length === 0 ? (
      <Tag color="default">All departments</Tag>
    ) : (
      ids.map((id) => (
        <Tag key={id} color="blue" style={{ marginBottom: 4 }}>
          {departmentMap.get(Number(id)) || `Dept #${id}`}
        </Tag>
      ))
    );

  const empChips = (ids: number[]) =>
    !ids || ids.length === 0 ? (
      <Tag color="default">All employees</Tag>
    ) : (
      <div className="d-flex flex-wrap gap-2">
        {ids.map((id) => {
          const e = employeeMap.get(Number(id));
          const name = e?.name || `Employee #${id}`;
          return (
            <div
              key={id}
              className="d-inline-flex align-items-center gap-2 px-2 py-1"
              style={{
                background: "#f1f5f9",
                borderRadius: 999,
                border: "1px solid #e2e8f0",
              }}
            >
              <EmployeeAvatar name={name} image={e?.image} size={24} />
              <span style={{ fontSize: 12.5, color: "#0f172a" }}>{name}</span>
            </div>
          );
        })}
      </div>
    );

  const appliesTo = (g: ReturnType<typeof groupHistoryExceptions>[number]) => {
    if (g.applies_to.type === "department") return deptChips(g.applies_to.department_ids);
    if (g.applies_to.type === "employees") return empChips(g.applies_to.employee_ids);
    return <Tag color="green">All employees</Tag>;
  };

  return (
    <Modal
      open={open}
      onCancel={onClose}
      footer={null}
      zIndex={1001}
      width="100%"
      style={{ top: 0, maxWidth: "100vw", paddingBottom: 0 }}
      styles={{
        content: {
          minHeight: "100vh",
          borderRadius: 0,
          padding: 0,
          display: "flex",
          flexDirection: "column",
        },
        header: {
          padding: "16px 24px",
          margin: 0,
          borderBottom: "1px solid #eef2f7",
        },
        body: {
          flex: 1,
          overflowY: "auto",
          padding: 24,
          background: "#f8fafc",
        },
        mask: { background: "rgba(15,23,42,0.55)" },
      }}
      title={
        <div className="d-flex align-items-center gap-3">
          <div
            style={{
              width: 38,
              height: 38,
              borderRadius: 10,
              background: "#10b9811f",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "#059669",
              fontSize: 19,
            }}
          >
            <i className="ti ti-file-spreadsheet" />
          </div>
          <div>
            <div style={{ fontWeight: 600, fontSize: 16, color: "#0f172a" }}>
              Payroll Report Details
            </div>
            <div style={{ fontSize: 12.5, color: "#94a3b8", fontWeight: 400 }}>
              {moment(record.month, "YYYY-MM").format("MMMM YYYY")} ·{" "}
              {record.file_name}
            </div>
          </div>
        </div>
      }
    >
      <div style={{ maxWidth: 1080, margin: "0 auto" }}>
        <div style={{ ...CARD_STYLE, marginBottom: 16 }}>
          <div className="row g-4">
            <div className="col-md-4">
              <Label>Downloaded By</Label>
              <div className="d-flex align-items-center gap-2">
                <EmployeeAvatar name={byName} image={byEmp?.image} size={36} />
                <span style={{ fontWeight: 600, color: "#0f172a" }}>{byName}</span>
              </div>
            </div>
            <div className="col-md-3">
              <Label>Downloaded On</Label>
              <div style={{ fontWeight: 500, color: "#0f172a" }}>
                {moment(record.created_at).format("DD MMM YYYY, HH:mm")}
              </div>
            </div>
            <div className="col-md-2">
              <Label>Month</Label>
              <Tag color="blue" style={{ margin: 0 }}>
                {moment(record.month, "YYYY-MM").format("MMM YYYY")}
              </Tag>
            </div>
            <div className="col-md-2">
              <Label>File Size</Label>
              <div style={{ fontWeight: 500, color: "#0f172a" }}>
                {formatBytes(record.file_size)}
              </div>
            </div>
            <div className="col-md-1">
              <Label>Template</Label>
              {record.template_id ? (
                <Tag color="geekblue" style={{ margin: 0 }}>
                  Yes
                </Tag>
              ) : (
                <span style={{ color: "#94a3b8" }}>—</span>
              )}
            </div>
          </div>
        </div>

        <div style={{ ...CARD_STYLE, marginBottom: 16 }}>
          <SectionTitle icon="ti ti-filter">Report Scope &amp; Settings</SectionTitle>
          <div className="row g-4">
            <div className="col-md-6">
              <Label>Departments</Label>
              <div className="d-flex flex-wrap gap-1">{deptChips(cfg.department_ids || [])}</div>
            </div>
            <div className="col-md-6">
              <Label>Employees</Label>
              {empChips(cfg.employee_ids || [])}
            </div>
            <div className="col-md-4">
              <Label>Late Cutoff</Label>
              <div style={{ fontWeight: 500 }}>{cfg.late_cutoff_time || "09:45"}</div>
            </div>
            <div className="col-md-4">
              <Label>Forgive Late Count</Label>
              <div style={{ fontWeight: 500 }}>
                {cfg.late_forgiveness_count ?? 0}
              </div>
            </div>
            <div className="col-md-4">
              <Label>Week-off Days</Label>
              <div className="d-flex flex-wrap gap-1">
                {(cfg.week_off_days && cfg.week_off_days.length
                  ? cfg.week_off_days
                  : [0]
                ).map((d: number) => (
                  <Tag key={d} color="default" style={{ margin: 0 }}>
                    {WEEKDAY_LABELS[d] ?? d}
                  </Tag>
                ))}
              </div>
            </div>
          </div>
        </div>

        <div style={CARD_STYLE}>
          <SectionTitle icon="ti ti-calendar-exclamation">
            Exceptions Applied ({groups.length})
          </SectionTitle>
          {groups.length === 0 ? (
            <div style={{ color: "#94a3b8", fontSize: 13 }}>
              No exceptions were applied to this report.
            </div>
          ) : (
            <div className="d-flex flex-column gap-3">
              {groups.map((g, idx) => (
                <div
                  key={idx}
                  style={{
                    border: "1px solid #e2e8f0",
                    borderRadius: 10,
                    padding: 14,
                    background: "#f8fafc",
                  }}
                >
                  <div className="row g-3">
                    <div className="col-md-4">
                      <Label>Dates ({g.dates.length})</Label>
                      <div className="d-flex flex-wrap gap-1">
                        {g.dates.map((d) => (
                          <Tag key={d} color="cyan" style={{ margin: 0 }}>
                            {moment(d).format("DD MMM")}
                          </Tag>
                        ))}
                      </div>
                    </div>
                    <div className="col-md-4">
                      <Label>Rules</Label>
                      <div className="d-flex flex-wrap gap-1">
                        {g.rules.map((r) => (
                          <Tag key={r} color="orange" style={{ margin: 0 }}>
                            {RULE_LABELS[r] || r}
                          </Tag>
                        ))}
                      </div>
                    </div>
                    <div className="col-md-4">
                      <Label>Applies To</Label>
                      <div className="d-flex flex-wrap gap-1">{appliesTo(g)}</div>
                    </div>
                    {g.remarks ? (
                      <div className="col-12">
                        <Label>Remarks</Label>
                        <div style={{ fontSize: 13, color: "#475569" }}>
                          {g.remarks}
                        </div>
                      </div>
                    ) : null}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </Modal>
  );
};

export default DownloadDetailModal;
